import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Shield, TrendingDown, TrendingUp, AlertTriangle, CheckCircle,
  Clock, X, Wallet, Zap, Info, ExternalLink, Trash2, RefreshCw,
  ChevronDown, ChevronUp, Activity, Target,
} from 'lucide-react';
import useTokenPrice from '../hooks/useTokenPrice';

// Symbol → CoinGecko id for live-price lookups. Wrapped tokens map to their
// underlying (Chainlink + DEX UX both treat them as the same asset).
const SO_SYMBOL_TO_CG = {
  WETH: 'ethereum',  ETH: 'ethereum',
  WBTC: 'bitcoin',   BTC: 'bitcoin',  cbBTC: 'bitcoin',  'BTC.b': 'bitcoin',
  WMATIC: 'matic-network', MATIC: 'matic-network', POL: 'matic-network',
  WBNB: 'binancecoin', BNB: 'binancecoin',
  WAVAX: 'avalanche-2', AVAX: 'avalanche-2',
  LINK: 'chainlink', UNI: 'uniswap', AAVE: 'aave',
  ARB: 'arbitrum', OP: 'optimism', SOL: 'solana',
  CRV: 'curve-dao-token', MKR: 'maker', COMP: 'compound-governance-token',
};
import {
  useAccount, useChainId, useSignTypedData,
  usePublicClient, useWalletClient, useSwitchChain,
} from 'wagmi';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { Spinner, InfoTip } from '../components/ui';
import TradingViewChart from '../components/TradingViewChart';
import {
  prepareDexStopOrder, submitDexStopOrder,
  listDexStopOrders, cancelDexStopOrder,
} from '../services/api';
import { CHAINS, PROTOCOL_CHAINS, getTokensFor, chartPairFor } from '../constants/dexTokens';
import DexCustomTokenInput from '../components/DexCustomTokenInput';

// Standard ERC-20 fragment for allowance + approve (same shape as the
// Limit Orders + DCA pages so the approval helper behaves identically).
const ERC20_ABI = [
  { name: 'allowance', type: 'function', stateMutability: 'view',
    inputs: [{ name: 'owner', type: 'address' }, { name: 'spender', type: 'address' }],
    outputs: [{ type: 'uint256' }] },
  { name: 'approve', type: 'function', stateMutability: 'nonpayable',
    inputs: [{ name: 'spender', type: 'address' }, { name: 'amount', type: 'uint256' }],
    outputs: [{ type: 'bool' }] },
];
const MAX_UINT256 = 2n ** 256n - 1n;
const COW_SPENDER = '0xC92E8bdf79f0507f65a392b0ab4667716BFE0110';

const EXPIRY_OPTIONS = [
  { label: '24 hours', sec: 86400 },
  { label: '7 days',   sec: 7 * 86400 },
  { label: '30 days',  sec: 30 * 86400 },
  { label: '90 days',  sec: 90 * 86400 },
];

const STATUS_STYLE = {
  armed:     { label: 'Armed',     cls: 'bg-amber-500/10 text-amber-400',   icon: Shield },
  triggered: { label: 'Triggered', cls: 'bg-blue-500/10 text-blue-300',    icon: Zap },
  expired:   { label: 'Expired',   cls: 'bg-dark-600 text-gray-400',       icon: Clock },
  cancelled: { label: 'Cancelled', cls: 'bg-dark-600 text-gray-400',       icon: X },
  failed:    { label: 'Failed',    cls: 'bg-red-500/10 text-red-400',      icon: AlertTriangle },
};

function toBaseUnits(humanAmount, decimals) {
  const s = String(humanAmount).trim();
  if (!/^\d+(\.\d+)?$/.test(s)) throw new Error('amount must be a non-negative decimal');
  const [whole, frac = ''] = s.split('.');
  const padded = (frac + '0'.repeat(decimals)).slice(0, decimals);
  const combined = (whole + padded).replace(/^0+/, '') || '0';
  if (combined === '0') throw new Error('amount must be > 0');
  return combined;
}
function formatBaseUnits(baseStr, decimals, maxFrac = 4) {
  if (!baseStr || baseStr === '0') return '0';
  const s = String(baseStr).padStart(decimals + 1, '0');
  const whole = s.slice(0, -decimals).replace(/^0+/, '') || '0';
  const frac  = s.slice(-decimals).slice(0, maxFrac).replace(/0+$/, '');
  return frac ? `${whole}.${frac}` : whole;
}
function fmtTime(iso) { try { return new Date(iso).toLocaleString(); } catch { return iso; } }
function fmtFutureTime(sec) {
  try { return new Date(sec * 1000).toLocaleString(); } catch { return String(sec); }
}

function Toast({ message, type = 'success', onDismiss }) {
  useEffect(() => { const t = setTimeout(onDismiss, 6000); return () => clearTimeout(t); }, [onDismiss]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl shadow-2xl text-sm font-medium max-w-md
      ${type === 'success' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
        : type === 'error' ? 'bg-red-500/20 text-red-300 border border-red-500/30'
        : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'}`}>
      {message}
    </div>
  );
}
function Card({ children, className = '' }) {
  return <div className={`bg-dark-800 rounded-2xl p-6 border border-dark-700/50 ${className}`}>{children}</div>;
}
function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.armed;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium ${s.cls}`}>
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
}

// ─── Create-stop-order panel ─────────────────────────────────────────────────
function CreateStopOrderPanel({ onCreated, onToast, onPairChange }) {
  const { address: connectedAddr, isConnected } = useAccount();
  const connectedChainId = useChainId();
  const { signTypedDataAsync, isPending: signing } = useSignTypedData();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();
  const { switchChain, isPending: switchingChain } = useSwitchChain();

  const [protocol, setProtocol] = useState('cow');
  const [chainId,  setChainId]  = useState(connectedChainId || 1);
  const [sellSym,  setSellSym]  = useState('WETH');
  const [buySym,   setBuySym]   = useState('USDC');
  const [sellAmount, setSellAmount] = useState('');
  const [direction, setDirection]   = useState('stop_loss');
  const [triggerPrice, setTriggerPrice] = useState('');
  const [slippagePct, setSlippagePct]   = useState(1);
  const [expirySec, setExpirySec]       = useState(7 * 86400);
  const [submitting, setSubmitting] = useState(false);
  const [stage, setStage] = useState(null);
  const [currentPrice, setCurrentPrice] = useState(null);
  const [customTick, setCustomTick] = useState(0);

  const tokens = useMemo(() => getTokensFor(chainId), [chainId, customTick]);
  const sellToken = tokens.find(t => t.symbol === sellSym);
  const buyToken  = tokens.find(t => t.symbol === buySym);
  const availableChains = CHAINS.filter(c => PROTOCOL_CHAINS[protocol].includes(c.id));

  useEffect(() => {
    const list = getTokensFor(chainId);
    if (!list.find(t => t.symbol === sellSym)) setSellSym(list[0]?.symbol || '');
    if (!list.find(t => t.symbol === buySym))  setBuySym(list[1]?.symbol || list[0]?.symbol || '');
    // If sell + buy collided (e.g. user flipped sell to match buy and the buy
    // dropdown silently filtered the option but state stayed stale), pick a
    // different buy token automatically.
    if (sellSym && buySym && sellSym === buySym) {
      const alt = list.find(t => t.symbol !== sellSym);
      if (alt) setBuySym(alt.symbol);
    }
  }, [chainId, sellSym, buySym]);

  useEffect(() => {
    onPairChange?.({ sellSym, buySym, chainId });
  }, [sellSym, buySym, chainId, onPairChange]);

  useEffect(() => {
    if (!PROTOCOL_CHAINS[protocol].includes(chainId)) {
      setChainId(PROTOCOL_CHAINS[protocol][0]);
    }
  }, [protocol, chainId]);

  // Trigger-price sanity hint: warn if user's stop-loss is above current price
  // or take-profit is below current price (would fire immediately).
  const triggerWarning = useMemo(() => {
    if (currentPrice == null || !triggerPrice) return null;
    const tp = Number(triggerPrice);
    if (!Number.isFinite(tp) || tp <= 0) return null;
    if (direction === 'stop_loss' && tp >= currentPrice) {
      return `Stop-loss at $${tp} is at or above current price ($${currentPrice.toFixed(4)}) — this would fire immediately.`;
    }
    if (direction === 'take_profit' && tp <= currentPrice) {
      return `Take-profit at $${tp} is at or below current price ($${currentPrice.toFixed(4)}) — this would fire immediately.`;
    }
    return null;
  }, [triggerPrice, direction, currentPrice]);

  // Compute the disabled reason explicitly — so users see WHY the button is
  // greyed out instead of clicking it in vain. Returns null if everything's OK.
  const disabledReason = (() => {
    if (!isConnected || !connectedAddr) return 'Connect your wallet first';
    if (Number(connectedChainId) !== Number(chainId))
      return `Wallet is on chain ${connectedChainId}, not ${CHAINS.find(c => c.id === Number(chainId))?.label || chainId}`;
    if (!sellToken) return `Sell token "${sellSym}" not found on this chain`;
    if (!buyToken)  return `Buy token "${buySym}" not found on this chain`;
    if (sellToken.symbol === buyToken.symbol) return 'Sell and buy tokens must be different';
    if (!(Number(sellAmount) > 0))    return 'Enter a sell amount > 0';
    if (!(Number(triggerPrice) > 0))  return 'Enter a trigger price > 0';
    if (signing)    return 'Waiting for wallet signature…';
    if (submitting) return 'Submitting…';
    return null;
  })();
  const canSubmit = disabledReason == null;

  async function ensureAllowance(spender, requiredAmount) {
    setStage('checking-allowance');
    async function read() {
      return publicClient.readContract({
        address: sellToken.address, abi: ERC20_ABI,
        functionName: 'allowance', args: [connectedAddr, spender],
      });
    }
    let allowance = await read();
    if (allowance >= requiredAmount) return;
    if (allowance > 0n) {
      setStage('approving');
      onToast({ type: 'info', message: `${sellSym} needs allowance reset to zero first — confirm in wallet.` });
      await walletClient.writeContract({
        address: sellToken.address, abi: ERC20_ABI,
        functionName: 'approve', args: [spender, 0n],
      });
      const start = Date.now();
      while (Date.now() - start < 180_000) {
        await new Promise(r => setTimeout(r, 4000));
        try { allowance = await read(); } catch {}
        if (allowance === 0n) break;
      }
      if (allowance !== 0n) throw new Error('Allowance reset did not confirm in 3 min');
    }
    setStage('approving');
    onToast({ type: 'info', message: `Approve ${sellSym} once for stop orders. ~15-30s for on-chain confirmation.` });
    await walletClient.writeContract({
      address: sellToken.address, abi: ERC20_ABI,
      functionName: 'approve', args: [spender, MAX_UINT256],
    });
    const start = Date.now();
    while (Date.now() - start < 180_000) {
      await new Promise(r => setTimeout(r, 4000));
      try { allowance = await read(); } catch {}
      if (allowance >= requiredAmount) return;
    }
    throw new Error('Approval did not confirm in 3 min');
  }

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const sellAmountBase = toBaseUnits(sellAmount, sellToken.decimals);
      const expiresAt = Math.floor(Date.now() / 1000) + Number(expirySec);

      // 1. Prepare on backend — returns typedData + spender + currentPrice
      setStage('signing');
      const prep = await prepareDexStopOrder({
        protocol, chainId,
        direction, triggerPrice: Number(triggerPrice),
        owner: connectedAddr,
        sellToken: sellToken.address, buyToken: buyToken.address,
        sellSymbol: sellToken.symbol, buySymbol: buyToken.symbol,
        sellDecimals: sellToken.decimals, buyDecimals: buyToken.decimals,
        sellAmount: sellAmountBase,
        slippagePct: Number(slippagePct),
        expiresAt,
      });
      const { stopOrderId, typedData, order, spender, minBuyAmount, currentPrice: serverPrice } = prep.data;
      setCurrentPrice(serverPrice);

      // 2. Approve sell token (one-time per chain/spender)
      await ensureAllowance(spender, BigInt(sellAmountBase));

      // 3. Sign the EIP-712 order
      setStage('signing');
      const signature = await signTypedDataAsync({
        domain: typedData.domain,
        types: typedData.types,
        primaryType: typedData.primaryType,
        message: order,
      });

      // 4. Persist on backend — worker takes over from here
      setStage('submitting');
      const result = await submitDexStopOrder({
        stopOrderId, protocol, chainId, owner: connectedAddr,
        direction, triggerPrice: Number(triggerPrice),
        sellToken: sellToken.address, buyToken: buyToken.address,
        sellSymbol: sellToken.symbol, buySymbol: buyToken.symbol,
        sellDecimals: sellToken.decimals, buyDecimals: buyToken.decimals,
        sellAmount: sellAmountBase, minBuyAmount,
        slippagePct: Number(slippagePct),
        expiresAt,
        order, signature,
      });

      const dirLabel = direction === 'stop_loss' ? 'Stop-loss' : 'Take-profit';
      onToast({ type: 'success', message: `${dirLabel} armed — Chainlink watcher will fire at $${triggerPrice} for ${sellSym}.` });
      setSellAmount(''); setTriggerPrice('');
      onCreated(result.data?.record);
    } catch (err) {
      const msg = err?.shortMessage || err?.message || 'Could not create stop order';
      onToast({ type: 'error', message: msg });
    } finally {
      setSubmitting(false);
      setStage(null);
    }
  }

  return (
    <Card>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-accent-primary/10 text-accent-primary flex items-center justify-center">
          <Shield className="w-4 h-4" />
        </div>
        <div>
          <h3 className="text-white font-semibold text-sm">New Stop Order</h3>
          <p className="text-gray-500 text-xs">Chainlink-triggered. Signs once. Fires automatically when your price hits.</p>
        </div>
      </div>

      {!isConnected && (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl px-4 py-3 mb-4 space-y-3">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-300 text-sm font-semibold">Connect your wallet first</p>
              <p className="text-amber-300/80 text-xs mt-0.5">We'll never ask for your private key — only EIP-712 signatures.</p>
            </div>
          </div>
          <ConnectButton.Custom>
            {({ openConnectModal }) => (
              <button onClick={openConnectModal}
                className="w-full py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:from-amber-400 hover:to-amber-500 transition-all flex items-center justify-center gap-2">
                <Wallet className="w-4 h-4" /> Connect Wallet
              </button>
            )}
          </ConnectButton.Custom>
        </div>
      )}
      {isConnected && connectedAddr && (
        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl px-4 py-2 mb-4 flex items-center gap-2 text-xs">
          <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
          <span className="text-emerald-300">Connected as <code className="font-mono">{connectedAddr.slice(0,6)}…{connectedAddr.slice(-4)}</code></span>
        </div>
      )}
      {isConnected && Number(connectedChainId) !== Number(chainId) && (
        <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl px-4 py-3 mb-4 space-y-3">
          <div className="flex items-start gap-3">
            <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
            <p className="text-blue-300 text-xs">
              Your wallet is on <span className="font-semibold">{CHAINS.find(c => c.id === Number(connectedChainId))?.label || `#${connectedChainId}`}</span>.
              This stop order needs to be signed on <span className="font-semibold">{CHAINS.find(c => c.id === chainId)?.label}</span>.
            </p>
          </div>
          <button onClick={() => switchChain?.({ chainId: Number(chainId) })} disabled={switchingChain || !switchChain}
            className="w-full py-2.5 rounded-xl text-sm font-bold bg-blue-500 hover:bg-blue-400 text-white transition-colors flex items-center justify-center gap-2 disabled:opacity-40">
            {switchingChain ? <Spinner size="sm" /> : <Info className="w-4 h-4" />}
            {switchingChain ? 'Switching…' : `Switch wallet to ${CHAINS.find(c => c.id === Number(chainId))?.label}`}
          </button>
        </div>
      )}

      <div className="space-y-4">
        {/* Direction toggle */}
        <div>
          <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 flex items-center gap-1">
            Type
            <InfoTip side="right">Stop-loss = capital preservation (sell when price drops to trigger). Take-profit = lock in gains (sell when price rises to trigger). The page warns you if your trigger would fire immediately.</InfoTip>
          </label>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setDirection('stop_loss')}
              className={`px-3 py-2 rounded-xl text-left border transition-colors ${
                direction === 'stop_loss' ? 'bg-red-500/10 border-red-500/40 text-white' : 'bg-dark-700 border-dark-600 text-gray-400 hover:bg-dark-600'}`}>
              <div className="flex items-center gap-2">
                <TrendingDown className="w-3.5 h-3.5 text-red-400" />
                <span className="text-sm font-semibold">Stop-loss</span>
              </div>
              <p className="text-[10px] text-gray-500 mt-0.5">Sell if price <span className="font-semibold">drops below</span> trigger</p>
            </button>
            <button type="button" onClick={() => setDirection('take_profit')}
              className={`px-3 py-2 rounded-xl text-left border transition-colors ${
                direction === 'take_profit' ? 'bg-emerald-500/10 border-emerald-500/40 text-white' : 'bg-dark-700 border-dark-600 text-gray-400 hover:bg-dark-600'}`}>
              <div className="flex items-center gap-2">
                <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-sm font-semibold">Take-profit</span>
              </div>
              <p className="text-[10px] text-gray-500 mt-0.5">Sell if price <span className="font-semibold">rises above</span> trigger</p>
            </button>
          </div>
        </div>

        {/* Protocol */}
        <div>
          <label className="text-gray-500 text-xs uppercase tracking-wide block mb-1.5">Protocol</label>
          <div className="grid grid-cols-2 gap-2">
            {['cow', 'velora'].map(p => (
              <button key={p} type="button" onClick={() => setProtocol(p)}
                className={`px-3 py-2 rounded-xl text-xs font-medium transition-colors border ${
                  protocol === p ? 'bg-accent-primary/10 border-accent-primary/40 text-white' : 'bg-dark-700 border-dark-600 text-gray-400 hover:bg-dark-600'}`}>
                {p === 'cow' ? 'CoW (Eth/Arb/Base)' : 'Velora (+Poly/BSC/OP/Avax)'}
              </button>
            ))}
          </div>
        </div>

        {/* Chain */}
        <div>
          <label className="text-gray-500 text-xs uppercase tracking-wide block mb-1.5">Network</label>
          <select value={chainId} onChange={e => setChainId(Number(e.target.value))}
            className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary">
            {availableChains.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>

        {/* Sell + amount */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide block mb-1.5">You sell</label>
            <select value={sellSym} onChange={e => setSellSym(e.target.value)}
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary">
              {tokens.map(t => <option key={t.address} value={t.symbol}>{t.symbol}{t.custom ? ' (custom)' : ''}</option>)}
            </select>
          </div>
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide block mb-1.5">Amount</label>
            <input type="number" min="0" step="any" value={sellAmount} onChange={e => setSellAmount(e.target.value)}
              placeholder="0.0"
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary placeholder-gray-600" />
          </div>
        </div>

        {/* Buy */}
        <div>
          <label className="text-gray-500 text-xs uppercase tracking-wide block mb-1.5">You receive (when fired)</label>
          <select value={buySym} onChange={e => setBuySym(e.target.value)}
            className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary">
            {tokens.filter(t => t.symbol !== sellSym).map(t => <option key={t.address} value={t.symbol}>{t.symbol}{t.custom ? ' (custom)' : ''}</option>)}
          </select>
        </div>

        {/* Custom token */}
        <DexCustomTokenInput chainId={chainId}
          onResolved={(tok) => { setCustomTick(t => t + 1); setBuySym(tok.symbol); }} />

        {/* Trigger price */}
        <div>
          <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 flex items-center gap-1">
            Trigger price <span className="text-gray-600">(USD per {sellSym})</span>
            <InfoTip side="right">Chainlink oracle price, not DEX spot. Chainlink is sticky (updates on 0.5-1% deviation or hourly heartbeat) — may lag spot by 30-60 sec. This is manipulation resistance, not a bug.</InfoTip>
          </label>
          <input type="number" min="0" step="any" value={triggerPrice}
            onChange={e => setTriggerPrice(e.target.value)} placeholder="0.0"
            className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary placeholder-gray-600" />
          {triggerWarning && (
            <p className="text-amber-400 text-[11px] mt-1 flex items-start gap-1">
              <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" /> {triggerWarning}
            </p>
          )}
          <p className="text-gray-500 text-[10px] mt-1">Price is checked once per minute via Chainlink oracle on chain.</p>
        </div>

        {/* Slippage */}
        <div>
          <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 flex items-center gap-1">
            Slippage tolerance
            <InfoTip side="right">Max acceptable price slippage at fill. 1% is the safe default for blue chips. Too tight (under 1%) on volatile pairs = order may not fill at trigger. Too loose (over 3%) = poor fills during fast moves.</InfoTip>
          </label>
          <div className="flex items-center gap-2">
            <input type="number" min="0.1" max="10" step="0.1" value={slippagePct}
              onChange={e => setSlippagePct(e.target.value)}
              className="w-24 bg-dark-700 border border-dark-600 rounded-lg px-2 py-1.5 text-white text-sm text-center focus:outline-none focus:border-accent-primary" />
            <span className="text-gray-500 text-xs">% — receive at least {(100 - Number(slippagePct || 0)).toFixed(1)}% of trigger × sell amount</span>
          </div>
        </div>

        {/* Expiry */}
        <div>
          <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 flex items-center gap-1">
            Watch for
            <InfoTip side="right">Auto-cancel after this period if trigger never hits. Tactical hedges → 24h-7d. Longer-term insurance → 30d-90d. You can cancel manually at any time before fire.</InfoTip>
          </label>
          <div className="grid grid-cols-4 gap-2">
            {EXPIRY_OPTIONS.map(opt => (
              <button key={opt.sec} type="button" onClick={() => setExpirySec(opt.sec)}
                className={`px-3 py-2 rounded-xl text-xs font-medium transition-colors ${
                  expirySec === opt.sec ? 'bg-accent-primary text-dark-900' : 'bg-dark-700 text-gray-400 hover:bg-dark-600'}`}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <button onClick={handleSubmit} disabled={!canSubmit}
          className="w-full btn-primary py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
          {(submitting || signing) ? <Spinner size="sm" /> : <Shield className="w-4 h-4" />}
          {stage === 'checking-allowance' ? 'Checking allowance…' :
           stage === 'approving' ? `Approving ${sellSym}…` :
           stage === 'signing' || signing ? 'Waiting for wallet signature…' :
           stage === 'submitting' ? 'Saving stop order…' :
           direction === 'stop_loss' ? 'Arm stop-loss' : 'Arm take-profit'}
        </button>
        {disabledReason ? (
          <p className="text-amber-400 text-[11px] text-center flex items-center justify-center gap-1">
            <AlertTriangle className="w-3 h-3" /> {disabledReason}
          </p>
        ) : (
          <p className="text-gray-500 text-[11px] text-center">
            The order is held off-chain until the Chainlink price crosses your trigger. No gas paid by you on fills.
          </p>
        )}
      </div>
    </Card>
  );
}

// ─── Order row ───────────────────────────────────────────────────────────────
function StopOrderRow({ record, onCancel, busy }) {
  const [expanded, setExpanded] = useState(false);
  const sellHuman = formatBaseUnits(record.sellAmount, record.sellDecimals || 18);
  const buyHuman  = record.minBuyAmount ? formatBaseUnits(record.minBuyAmount, record.buyDecimals || 18) : null;
  const dirLabel = record.direction === 'stop_loss' ? 'Stop-loss' : 'Take-profit';
  const DirIcon = record.direction === 'stop_loss' ? TrendingDown : TrendingUp;
  const protoLabel = record.protocol === 'cow' ? 'CoW' : 'Velora';

  // Live price of the watched asset (the SELL token — that's what Chainlink
  // tracks for the trigger condition).
  const cgId = SO_SYMBOL_TO_CG[record.sellSymbol] || null;
  const { data: liveData } = useTokenPrice(cgId, 30000);
  const livePrice = liveData?.currentPriceUsd ?? liveData?.market_data?.current_price?.usd ?? null;

  // Distance to trigger
  let distancePct = null, distanceUsd = null, willFireSoon = false;
  if (Number.isFinite(livePrice) && Number.isFinite(record.triggerPrice)) {
    distanceUsd = livePrice - Number(record.triggerPrice);
    distancePct = (distanceUsd / Number(record.triggerPrice)) * 100;
    // "Soon" = within 2% in the direction of the trigger
    willFireSoon = record.direction === 'stop_loss'
      ? (distancePct >= 0 && distancePct < 2)
      : (distancePct <= 0 && distancePct > -2);
  }

  function copyToClipboard(text) { try { navigator.clipboard?.writeText(text); } catch {} }

  return (
    <div className="bg-dark-700/40 rounded-xl border border-dark-600/40 overflow-hidden hover:border-dark-500/60 transition-colors">
      <button onClick={() => setExpanded(e => !e)}
        className="w-full px-4 py-3 text-left transition-colors hover:bg-dark-600/20">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <DirIcon className={`w-3.5 h-3.5 ${record.direction === 'stop_loss' ? 'text-red-400' : 'text-emerald-400'}`} />
              <span className="text-white text-sm font-semibold">
                {dirLabel}: {sellHuman} {record.sellSymbol} → {record.buySymbol}
              </span>
              <StatusBadge status={record.status} />
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-dark-600 text-gray-400 border border-dark-500">{protoLabel}</span>
              {willFireSoon && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30 font-semibold animate-pulse">
                  near trigger
                </span>
              )}
            </div>
            <p className="text-gray-400 text-xs mt-1">
              Trigger: <span className="font-semibold text-white">${record.triggerPrice}</span> ·{' '}
              Slippage {record.slippagePct}% · Chain {record.chainId}
              {distancePct !== null && record.status === 'armed' && (
                <> · <span className={Math.abs(distancePct) < 5 ? 'text-amber-300' : 'text-gray-400'}>
                  {distancePct > 0 ? '+' : ''}{distancePct.toFixed(2)}% from current
                </span></>
              )}
            </p>
            {record.status === 'armed' && (
              <p className="text-gray-500 text-xs mt-0.5">
                {record.lastPriceCheck != null
                  ? <>Last price check: <span className="font-mono text-gray-300">${Number(record.lastPriceCheck).toFixed(4)}</span> · expires {fmtFutureTime(record.expiresAt)}</>
                  : <>Expires {fmtFutureTime(record.expiresAt)}</>}
              </p>
            )}
            {record.status === 'triggered' && record.triggeredAt && (
              <p className="text-blue-300 text-xs mt-0.5">
                Fired {fmtTime(record.triggeredAt)} · now a live limit order on {protoLabel} (track on Limit Orders tab)
              </p>
            )}
            {record.status === 'failed' && record.error && (
              <p className="text-red-400 text-xs mt-0.5">Error: {record.error}</p>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
            {record.status === 'armed' && (
              <button onClick={() => onCancel(record.stopOrderId)} disabled={busy}
                title="Cancel — stop watching this trigger"
                className="p-1.5 rounded-lg bg-dark-700 text-red-400 hover:bg-red-500/10 disabled:opacity-50">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
            <span className="ml-1 text-gray-500" onClick={() => setExpanded(e => !e)}>
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </span>
          </div>
        </div>
      </button>

      {expanded && (
        <div className="px-4 py-3 border-t border-dark-600/40 bg-dark-800/40 space-y-3 animate-[so-fade_0.2s_ease-out]">
          <style>{`@keyframes so-fade { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }`}</style>
          {/* Trigger snapshot */}
          {cgId && (
            <div className="bg-gradient-to-r from-dark-700/50 to-dark-700/20 rounded-lg px-3 py-2.5 border border-dark-600">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold flex items-center gap-1.5">
                    <Activity className="w-3 h-3 text-emerald-400 animate-pulse" />
                    Live {record.sellSymbol} price
                  </div>
                  <div className="text-base font-mono font-semibold text-white mt-0.5">
                    {livePrice != null ? `$${Number(livePrice).toFixed(livePrice >= 1 ? 2 : 4)}` : '—'}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold flex items-center gap-1 justify-end">
                    <Target className="w-3 h-3" /> Trigger
                  </div>
                  <div className={`text-base font-mono font-semibold mt-0.5 ${record.direction === 'stop_loss' ? 'text-red-400' : 'text-emerald-400'}`}>
                    ${Number(record.triggerPrice).toFixed(record.triggerPrice >= 1 ? 2 : 4)}
                  </div>
                </div>
              </div>
              {distancePct !== null && (
                <div className="mt-2 pt-2 border-t border-dark-600">
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Distance to trigger</div>
                  <div className="text-sm font-mono">
                    <span className={Math.abs(distancePct) < 5 ? 'text-amber-300' : 'text-gray-300'}>
                      {distanceUsd > 0 ? '+' : ''}${Math.abs(distanceUsd).toFixed(2)}
                    </span>
                    <span className="text-gray-500 ml-2">
                      ({distancePct > 0 ? '+' : ''}{distancePct.toFixed(2)}%)
                    </span>
                  </div>
                  <p className="text-[10px] text-gray-500 mt-1">
                    {record.direction === 'stop_loss'
                      ? 'Stop fires when price drops to or below trigger.'
                      : 'Take-profit fires when price rises to or above trigger.'}
                  </p>
                </div>
              )}
            </div>
          )}
          {/* Amounts */}
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-dark-700/40 rounded-lg px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Sell</div>
              <div className="text-xs text-white font-mono">{sellHuman} {record.sellSymbol}</div>
              <div className="text-[10px] text-gray-500 font-mono mt-0.5 truncate" title={record.sellAmount}>{record.sellAmount} base</div>
            </div>
            {buyHuman && (
              <div className="bg-dark-700/40 rounded-lg px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Min buy</div>
                <div className="text-xs text-white font-mono">~{buyHuman} {record.buySymbol}</div>
                <div className="text-[10px] text-gray-500 font-mono mt-0.5 truncate" title={record.minBuyAmount}>{record.minBuyAmount} base</div>
              </div>
            )}
          </div>
          {/* Stop order ID */}
          <div className="bg-dark-700/40 rounded-lg px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Stop order ID</div>
            <button onClick={() => copyToClipboard(record.stopOrderId)}
              title="Copy"
              className="text-xs font-mono text-white break-all hover:text-accent-primary transition-colors text-left">
              {record.stopOrderId}
            </button>
            {record.orderbookOrderId && (
              <>
                <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mt-2 mb-1">Underlying limit order</div>
                <button onClick={() => copyToClipboard(record.orderbookOrderId)} title="Copy"
                  className="text-xs font-mono text-blue-300 break-all hover:text-blue-200 transition-colors text-left">
                  {record.orderbookOrderId}
                </button>
              </>
            )}
          </div>
          {/* Lifecycle */}
          <div className="bg-dark-700/40 rounded-lg px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1.5">Lifecycle</div>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between"><span className="text-gray-400">Created</span><span className="text-white">{fmtTime(record.createdAt)}</span></div>
              {record.armedAt && (
                <div className="flex justify-between"><span className="text-emerald-400">Armed</span><span className="text-white">{fmtTime(record.armedAt)}</span></div>
              )}
              {record.triggeredAt && (
                <div className="flex justify-between"><span className="text-blue-300">Triggered</span><span className="text-white">{fmtTime(record.triggeredAt)}</span></div>
              )}
              {record.submittedAt && (
                <div className="flex justify-between"><span className="text-blue-300">Submitted to orderbook</span><span className="text-white">{fmtTime(record.submittedAt)}</span></div>
              )}
              {record.cancelledAt && (
                <div className="flex justify-between"><span className="text-red-400">Cancelled</span><span className="text-white">{fmtTime(record.cancelledAt)}</span></div>
              )}
              {record.lastPriceCheckAt && (
                <div className="flex justify-between"><span className="text-gray-500">Last price check</span><span className="text-gray-400">{fmtTime(record.lastPriceCheckAt)}</span></div>
              )}
              <div className="flex justify-between"><span className="text-gray-500">Expires</span><span className="text-gray-400">{fmtFutureTime(record.expiresAt)}</span></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────
export default function DexStopOrdersPage() {
  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast]     = useState(null);
  const [busy, setBusy]       = useState(null);
  const [chartPair, setChartPair] = useState('ETH/USDT');

  const reload = useCallback(async () => {
    setLoading(true);
    try { const r = await listDexStopOrders(); setOrders(r.data?.orders || []); }
    catch (err) { setToast({ type: 'error', message: err?.message || 'Could not load stop orders' }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const handleCreated = (record) => {
    if (record) setOrders(prev => [record, ...prev.filter(o => o.stopOrderId !== record.stopOrderId)]);
  };

  const handleCancel = async (stopOrderId) => {
    setBusy(stopOrderId);
    try {
      const r = await cancelDexStopOrder(stopOrderId);
      setOrders(prev => prev.map(o => o.stopOrderId === stopOrderId ? r.data?.record || o : o));
      setToast({ type: 'success', message: 'Stop order cancelled. Watcher stopped.' });
    } catch (err) {
      setToast({ type: 'error', message: err?.message || 'Cancel failed' });
    } finally { setBusy(null); }
  };

  const active = orders.filter(o => o.status === 'armed');
  const history = orders.filter(o => o.status !== 'armed');

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-accent-primary/15 text-accent-primary flex items-center justify-center">
            <Shield className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">DEX Stop Orders</h1>
            <p className="text-gray-400 text-sm">Stop-loss + take-profit triggered by Chainlink oracle prices. Sign once; the platform watches and fires.</p>
          </div>
        </div>
      </div>

      <Card className="!p-3">
        <div className="flex items-center justify-between mb-2 px-2 pt-1">
          <div className="flex items-center gap-2">
            <span className="text-gray-400 text-xs uppercase tracking-wide">Reference chart</span>
            <span className="text-white text-sm font-mono">{chartPair}</span>
          </div>
          <span className="text-[10px] text-gray-500">Powered by TradingView · price reference only</span>
        </div>
        <TradingViewChart symbol={chartPair} interval="60" height={320} />
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CreateStopOrderPanel onCreated={handleCreated} onToast={setToast}
          onPairChange={({ sellSym, buySym }) => setChartPair(chartPairFor(sellSym, buySym))} />

        <Card>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-white font-semibold text-sm">Armed orders</h3>
              <p className="text-gray-500 text-xs">{active.length} watching · Chainlink polled every minute</p>
            </div>
            <button onClick={reload} disabled={loading}
              className="btn-ghost text-xs px-3 py-1.5 flex items-center gap-1.5">
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Reload
            </button>
          </div>
          {loading ? (
            <div className="flex justify-center py-10"><Spinner /></div>
          ) : active.length === 0 ? (
            <div className="text-center py-10">
              <Shield className="w-8 h-8 text-gray-600 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">No armed stop orders. Create one to protect a position.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {active.map(o => (
                <StopOrderRow key={o.stopOrderId} record={o} onCancel={handleCancel} busy={busy === o.stopOrderId} />
              ))}
            </div>
          )}
        </Card>
      </div>

      {history.length > 0 && (
        <Card>
          <h3 className="text-white font-semibold text-sm mb-3">History</h3>
          <div className="space-y-2">
            {history.map(o => (
              <StopOrderRow key={o.stopOrderId} record={o} onCancel={handleCancel} busy={busy === o.stopOrderId} />
            ))}
          </div>
        </Card>
      )}

      {toast && <Toast {...toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}
